import { ref, nextTick, type Ref } from "vue";
import type {Workflow} from "@/types";
import { getLayoutedElements } from "@/composables/dagre-layout";

export function useWorkflowCanvas(
  workflow: Ref<Workflow | undefined>,
  selectedVersion: Ref<string>,
  nodes: Ref<any[]>,
  edges: Ref<any[]>,
  currentNodes: Ref<any[]>,
  canvasMode: Ref<string>,
  isTransitioning: Ref<boolean>,
  savedViewport: Ref<{ x: number; y: number; zoom: number } | null>,
  setViewport: (viewport: { x: number; y: number; zoom: number }) => void,
  fitView: (options?: { padding?: number }) => void
) {
  const flowDirection = ref<"TB" | "LR">("TB"); // TB = Top-Bottom, LR = Left-Right

  const nodePositions = ref<
    Map<
      string,
      {
        TB: Map<string, { x: number; y: number }>;
        LR: Map<string, { x: number; y: number }>;
      }
    >
  >(new Map());

  function getPositionKey() {
    return `${workflow.value?.id ?? ''}_${selectedVersion.value}`;
  }

  function getPositionNodeId(nodeId: string): string {
    const mapping: Record<string, string> = {
      h1: "1",
      h2: "2",
      h3: "3",
      h4: "4",
      h5: "5",
      h6: "6",
    };
    return mapping[nodeId] || nodeId;
  }

  function saveNodePositions() {
    const key = getPositionKey();
    const direction = flowDirection.value;

    if (!nodePositions.value.has(key)) {
      nodePositions.value.set(key, { TB: new Map(), LR: new Map() });
    }

    const positions = nodePositions.value.get(key)![direction];
    currentNodes.value.forEach((node) => {
      const positionId = getPositionNodeId(node.id);
      positions.set(positionId, { ...node.position });
    });
  }

  function restoreViewport() {
    if (savedViewport.value) {
      setViewport(savedViewport.value!);

      setTimeout(() => {
        setViewport(savedViewport.value!);
        isTransitioning.value = false; // End transition after viewport is restored
      }, 30);
    } else {
      setTimeout(() => {
        isTransitioning.value = false;
      }, 30);
    }
  }

  function applyHierarchicalLayout() {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes.value,
      edges.value,
      flowDirection.value
    );

    nodes.value = layoutedNodes;
    edges.value = layoutedEdges;

    const key = getPositionKey();
    const direction = flowDirection.value;

    if (!nodePositions.value.has(key)) {
      nodePositions.value.set(key, { TB: new Map(), LR: new Map() });
    }

    const positions = nodePositions.value.get(key)![direction];
    layoutedNodes.forEach((node) => {
      const positionId = getPositionNodeId(node.id);
      positions.set(positionId, { ...node.position });
    });

    nextTick(() => fitView({ padding: 0.1 }));
  }

  function toggleFlowDirection() {
    saveNodePositions();
    flowDirection.value = flowDirection.value === "TB" ? "LR" : "TB";

    const key = getPositionKey();
    const positions = nodePositions.value.get(key);
    const hasPositions = positions && positions[flowDirection.value].size > 0;

    if (!hasPositions) {
      applyHierarchicalLayout();
    }
  }

  function setCanvasMode(mode: "editor" | "history") {
    saveNodePositions();
    isTransitioning.value = true;

    if (mode === "history") {
      canvasMode.value = "history";
    } else {
      canvasMode.value = "editor";
    }

    nextTick(() => setTimeout(() => restoreViewport(), 50));
  }

  function onNodeDragStop(event: any) {
    const { node } = event;
    const key = getPositionKey();
    const direction = flowDirection.value;

    if (!nodePositions.value.has(key)) {
      nodePositions.value.set(key, { TB: new Map(), LR: new Map() });
    }

    const positionId = getPositionNodeId(node.id);
    nodePositions.value
      .get(key)!
      [direction].set(positionId, { ...node.position });
  }

  return {
    // State
    flowDirection,
    nodePositions,

    // Functions
    saveNodePositions,
    restoreViewport,
    applyHierarchicalLayout,
    toggleFlowDirection,
    setCanvasMode,
    onNodeDragStop,
    getPositionKey,
    getPositionNodeId,
  };
}
